import {FC, ReactElement, ReactNode} from 'react'

import {Link, Trans} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Button, ButtonProps} from './Button'
import {ButtonVariant} from './ButtonTypes'
import {Text, TextFontWeight, TextVariant} from './Text'

const SectionButton: FC<ButtonProps> = (props) => (
  <Button
    type="button"
    variant={props.variant ?? ButtonVariant.primary}
    fullWidth={props.variant !== ButtonVariant.default}
    {...props}
  >
    {props.children}
  </Button>
)

export type SectionIntroProps = {
  label?: string
  title?: {text: string; variant?: TextVariant; maxWidth?: string; color?: string}
  description?: {
    text: string | ReactElement<typeof Trans>
    marginTop?: string
    maxWidth?: string
    color?: string
  }
  additionalContent?: ReactNode
  buttons?: (ButtonProps & {text: string; linkHref?: string})[]
  maxWidth?: string
  isFixedWidth?: boolean
  align?: 'left' | 'center'
}

export const SectionIntro: FC<SectionIntroProps> = ({
  label,
  title,
  description,
  additionalContent,
  buttons,
  maxWidth = '100%',
  isFixedWidth = false,
  align = 'center',
}) => (
  <>
    <div className="content">
      {label && (
        <div className="label">
          <Text
            variant={TextVariant.subtitle14}
            fontWeight={TextFontWeight.semibold}
            color={theme.palette.text.primary}
          >
            {label}
          </Text>
        </div>
      )}
      {title && (
        <div className="title">
          <Text variant={title.variant ?? TextVariant.h2} color={title.color}>
            {title.text}
          </Text>
        </div>
      )}
      {description && (
        <div className="description">
          <Text variant={TextVariant.text20} color={description.color ?? theme.palette.text.light}>
            {description.text}
          </Text>
        </div>
      )}
      {additionalContent}
      {buttons && (
        <div className="buttons">
          {buttons.map((button) => (
            <div key={button.text}>
              {button.linkHref ? (
                <Link href={button.linkHref} onClick={button.onClick as any}>
                  {/* button.onClick has type MouseEventHandler<HTMLButtonElement> while Link.onClick requires MouseEventHandler<HTMLAnchorElement> */}
                  <SectionButton {...button}>{button.text}</SectionButton>
                </Link>
              ) : (
                <SectionButton {...button}>{button.text}</SectionButton>
              )}
            </div>
          ))}
        </div>
      )}
    </div>

    <style jsx>{`
      .content {
        width: 100%;
        max-width: ${maxWidth};
        ${isFixedWidth ? `flex: 0 0 ${maxWidth}` : ''};
        ${align === 'center' ? 'margin: 0 auto' : ''};
        text-align: ${align};
      }
      .description {
        ${description?.maxWidth ? `max-width: ${description.maxWidth}` : ''};
        ${description?.maxWidth && align === 'center' ? `margin: 0 auto` : ''};
        margin-top: ${description?.marginTop ?? theme.spacing[3.5]};
      }
      .buttons {
        display: flex;
        margin-top: ${theme.spacing[5]};
        ${align === 'center' ? 'justify-content: center' : ''};
        gap: ${theme.spacing[2.5]};
      }
      .label {
        margin-bottom: ${theme.spacing[4]};
      }
      .title {
        ${title?.maxWidth ? `max-width: ${title.maxWidth}` : ''};
        ${title?.maxWidth && align === 'center' ? `margin: 0 auto` : ''};
      }
      @media ${USER_TABLET_SCREENSIZE} {
        .content {
          flex: 1;
          max-width: 640px;
          display: flex;
          align-items: center;
          flex-direction: column;
          text-align: center;
        }
      }
      @media ${MOBILE_SCREENSIZE} {
        .buttons {
          display: block;
          width: 100%;
        }
      }
    `}</style>
  </>
)
